/**
 * Axios layer for the redesigned Step 6 disbursement table (sc-45812), behind the
 * `settlementRedesign` flipper. One backend call per operation — the backend owns
 * partial-success semantics; the frontend never loops per-row requests (same
 * discipline as the liens bulk push/undo).
 *
 *   READ   GET  /settlements/{id}/disbursements
 *   SUBMIT POST /settlements/{id}/disbursements/submit  { rows }
 *   UNDO   POST /settlements/{id}/disbursements/undo    { rows }
 */
import axios from 'axios';
import type {
  CancelClientDisbursementResult,
  DisbursementEmailComposition,
  DisbursementsData,
  DisbursementRowRef,
  DisbursementRowResult,
  RawDisbursementsResponse,
  RawDisbursementSubmitResult,
  ResendDisbursementEmailResult,
  SendClientDisbursementPayload,
} from '../../models/disbursementTypes';
import {
  normalizeDisbursements,
  normalizeResults,
  toSubmitRequestBody,
} from '../../utils/disbursementHelpers';

const base = (settlementId: string): string =>
  `/settlements/${settlementId}/disbursements`;

/**
 * GET the disbursement rows (recipients, amounts, per-row status) for the table.
 * Accepts an optional AbortSignal so an in-flight read can be cancelled (e.g. a
 * newer load supersedes it, or the component unmounts).
 */
export const getDisbursements = async (
  settlementId: string,
  signal?: AbortSignal,
): Promise<DisbursementsData> => {
  const { data } = await axios.get<RawDisbursementsResponse>(base(settlementId), {
    signal,
  });
  return normalizeDisbursements(data);
};

/**
 * Submit the selected rows in one call. A single-row submit is a one-item list.
 * The firm-invoice row runs FinalizeInvoice -> PayFromTrust as one saga server-side.
 * Returns one result per requested row (never all-or-nothing).
 */
export const submitDisbursements = async (
  settlementId: string,
  rows: DisbursementRowRef[],
): Promise<DisbursementRowResult[]> => {
  const { data } = await axios.post<RawDisbursementSubmitResult>(
    `${base(settlementId)}/submit`,
    toSubmitRequestBody(rows),
  );
  return normalizeResults(data?.results);
};

/**
 * Record the client's disbursement as already paid outside Payabli (paper check, bank portal
 * ACH, wire): writes the QuickBooks check directly and books the audit row (sc-46621).
 *
 * The amount, date and method are all derived server-side from the settlement's snapshot, so
 * there is nothing to post. Undo goes through the ordinary client-net undo — the backend treats
 * it as the same operation for either push method.
 */
export const recordManualDisbursement = async (settlementId: string): Promise<void> => {
  await axios.post(`/settlements/${settlementId}/client-disbursement/manual`, {});
};

/**
 * Send the client's disbursement: creates the Payabli link, debits trust, writes the QBO
 * check and emails the client — all server-side, from this one call (sc-46619).
 *
 * Deliberately the only call the Send modal makes. resendDisbursementEmail is the resend path;
 * calling it here too would email the client twice.
 */
export const sendClientDisbursement = async (
  settlementId: string,
  payload: SendClientDisbursementPayload,
): Promise<void> => {
  await axios.post(`/settlements/${settlementId}/client-disbursement/send`, payload);
};

const disbursementEmailUrl = (settlementId: string, disbursementId: string): string =>
  `/settlements/${settlementId}/payabli-disbursements/${disbursementId}/email`;

/** The composition last sent for this disbursement, to pre-fill the resend modal (sc-47502). */
export const getDisbursementEmail = async (
  settlementId: string,
  disbursementId: string,
): Promise<DisbursementEmailComposition> => {
  const { data } = await axios.get<Partial<DisbursementEmailComposition>>(
    disbursementEmailUrl(settlementId, disbursementId),
  );
  // The API drops null fields entirely, so every optional one can arrive absent.
  return {
    clientName: data.clientName ?? '',
    mailingAddress: data.mailingAddress ?? '',
    to: data.to ?? '',
    ccs: data.ccs ?? '',
    bccs: data.bccs ?? '',
    subject: data.subject ?? '',
    body: data.body ?? '',
  };
};

/**
 * Resend the disbursement email without touching the payment link or the QuickBooks check
 * (sc-47502). Appends a new composition server-side; nothing is re-created at Payabli.
 */
export const resendDisbursementEmail = async (
  settlementId: string,
  disbursementId: string,
  payload: SendClientDisbursementPayload,
): Promise<ResendDisbursementEmailResult> => {
  const { data } = await axios.post<ResendDisbursementEmailResult>(
    disbursementEmailUrl(settlementId, disbursementId),
    payload,
  );
  return data;
};

/**
 * Cancel the client's payment link and reverse its QuickBooks check (sc-46620). Against a row
 * whose earlier reversal failed, the backend skips Payabli and retries the reversal alone.
 */
export const cancelClientDisbursement = async (
  settlementId: string,
): Promise<CancelClientDisbursementResult> => {
  const { data } = await axios.post<CancelClientDisbursementResult>(
    `/settlements/${settlementId}/client-disbursement/cancel`,
  );
  return data;
};

/** Undo (reverse the QBO entry for) the selected rows — mirror of submit. */
export const undoDisbursements = async (
  settlementId: string,
  rows: DisbursementRowRef[],
): Promise<DisbursementRowResult[]> => {
  const { data } = await axios.post<RawDisbursementSubmitResult>(
    `${base(settlementId)}/undo`,
    toSubmitRequestBody(rows),
  );
  return normalizeResults(data?.results);
};
